import React, { FC, memo } from "react";

import { StarRating } from "@at/design-system-elements";

import { mapChannelToVehicleType } from "@at/formatter-utils";
import { useFederatedQuery as useQuery } from "@at/sauron-platform-graphql";

import {
  ExpertReviewVideo,
  VideoDetails,
} from "./expert-review-video/expert-review-video";

import * as Styled from "./leasing-expert-review.styled";
import { ILeasingExpertReview } from "./leasing-expert-review.types";
import { LeasingExpertReviewQuery } from "./query/__generated__/leasing-expert-review.fed.query";
import { leasingExpertReviewQuery } from "./query/leasing-expert-review.fed.query";
import { SkeletonExpertReview } from "../../../pages/skeleton/skeleton-expert-review";
import { useChannel } from "../../../utils/hooks/use-channel";

const LeasingExpertReview: FC<ILeasingExpertReview> = ({
  make,
  model,
  bodyType,
}: ILeasingExpertReview) => {
  const channel = useChannel();
  const channelAsVehicleType = mapChannelToVehicleType(channel);
  const { data, loading, error } = useQuery<LeasingExpertReviewQuery>(
    leasingExpertReviewQuery,
    {
      variables: {
        make: make,
        model: model,
        bodyType: bodyType,
        vehicleTypes: [channelAsVehicleType],
      },
    },
  );

  const review = data?.content?.articles?.articles[0];

  if (loading || error) return <SkeletonExpertReview />;

  if (!review || review.__typename !== "ExpertReview") return null;

  const youtubeVideoId = review?.videoDetails?.id;

  const reviewSummary = review.summary?.trim() || "";

  const formattedReviewSummary = `"${reviewSummary}"`;

  const expertReviewLink = review.link;

  const mainImage = review.images?.[0]?.url?.replace("{resize}", "w640");

  const videoDetails = review?.videoDetails;

  const noMediaContent = !mainImage && !youtubeVideoId;

  let mappedVideoDetails;

  if (youtubeVideoId) {
    mappedVideoDetails = {
      videoReference: youtubeVideoId || "",
      title: videoDetails?.title || "",
      description: videoDetails?.description || "",
      uploadDate: videoDetails?.publishedAt || "",
      duration: videoDetails?.duration || "",
    } satisfies VideoDetails;
  }

  return (
    <Styled.ExpertReviewContainer as="section">
      {youtubeVideoId && (
        <Styled.ExpertReviewMediaContent>
          <Styled.ExpertReviewVideoContainer>
            <ExpertReviewVideo
              viewTrackingEventText={"leasing_expert_review_video_click"}
              videoDetails={mappedVideoDetails}
            />
          </Styled.ExpertReviewVideoContainer>
        </Styled.ExpertReviewMediaContent>
      )}
      {!youtubeVideoId && mainImage && (
        <Styled.ExpertReviewMediaContent
          data-testid="expert-review-image"
          data-gui="expert-review-image"
        >
          <Styled.ExpertReviewImageContainer>
            <Styled.ExpertReviewImage
              src={mainImage}
              alt=""
              height={9}
              width={16}
              loading="lazy"
            />
          </Styled.ExpertReviewImageContainer>
        </Styled.ExpertReviewMediaContent>
      )}
      <Styled.ExpertReviewTextContent $noMediaContent={noMediaContent}>
        <Styled.ExpertReviewHeading as="h3">{`Our review of the ${make} ${model}`}</Styled.ExpertReviewHeading>
        <Styled.ExpertReviewRatingBlock>
          <Styled.ExpertReviewOverallRatingBox>
            {review.overallRating}
          </Styled.ExpertReviewOverallRatingBox>
          <StarRating rating={review.overallRating} />
        </Styled.ExpertReviewRatingBlock>
        <Styled.ExpertReviewIntroText
          as="p"
          dangerouslySetInnerHTML={{
            __html: formattedReviewSummary,
          }}
        />
        <Styled.ExpertReviewButton
          displayType="secondary"
          text="Read the full review"
          url={expertReviewLink}
        />
      </Styled.ExpertReviewTextContent>
    </Styled.ExpertReviewContainer>
  );
};

export default memo(LeasingExpertReview);
